import { AxiosError, type AxiosResponseHeaders, type RawAxiosResponseHeaders } from 'axios'

export type ApiErrorKind =
  | 'network'
  | 'timeout'
  | 'canceled'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'business_rule'
  | 'validation'
  | 'rate_limited'
  | 'unavailable'
  | 'server'
  | 'unknown'

interface PydanticDetailItem {
  loc: Array<string | number>
  msg: string
  type: string
}

export interface ApiErrorBody {
  detail?: string | PydanticDetailItem[]
}

export interface RateLimitInfo {
  limit: number | undefined
  remaining: number | undefined
}

export interface ApiErrorOptions {
  kind: ApiErrorKind
  status: number
  message: string
  fieldErrors?: Record<string, string[]>
  retryAfterMs?: number
  rateLimit?: RateLimitInfo
  endpoint?: string
  method?: string
  cause?: unknown
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind
  readonly status: number
  readonly fieldErrors: Record<string, string[]> | undefined
  readonly retryAfterMs: number | undefined
  readonly rateLimit: RateLimitInfo | undefined
  readonly endpoint: string | undefined
  readonly method: string | undefined
  readonly originalError: unknown

  constructor(opts: ApiErrorOptions) {
    super(opts.message)
    this.name = 'ApiError'
    this.kind = opts.kind
    this.status = opts.status
    this.fieldErrors = opts.fieldErrors
    this.retryAfterMs = opts.retryAfterMs
    this.rateLimit = opts.rateLimit
    this.endpoint = opts.endpoint
    this.method = opts.method
    this.originalError = opts.cause
  }

  get isRetryable(): boolean {
    return (
      this.kind === 'network' ||
      this.kind === 'timeout' ||
      this.kind === 'server' ||
      this.kind === 'unavailable'
    )
  }
}

const FALLBACK_MESSAGES: Record<ApiErrorKind, string> = {
  network: 'Unable to reach the server. Check your connection and try again.',
  timeout: 'The request took too long to complete. Please try again.',
  canceled: 'Request was canceled.',
  unauthorized: 'Your session has expired. Please sign in again.',
  forbidden: 'You do not have access to this resource.',
  not_found: 'The requested resource was not found.',
  business_rule: 'The request could not be completed. Please review the details and try again.',
  validation: 'Some of the submitted values are invalid.',
  rate_limited: 'Too many requests. Please slow down and try again shortly.',
  unavailable: 'This service is temporarily unavailable. Please try again in a moment.',
  server: 'The server ran into a problem. Please try again in a moment.',
  unknown: 'Something went wrong. Please try again.',
}

function kindFromStatus(status: number): ApiErrorKind {
  if (status === 400) return 'business_rule'
  if (status === 401) return 'unauthorized'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'not_found'
  if (status === 422) return 'validation'
  if (status === 429) return 'rate_limited'
  if (status === 499) return 'canceled'
  if (status === 503) return 'unavailable'
  if (status === 504) return 'timeout'
  if (status >= 500) return 'server'
  if (status >= 400) return 'unknown'
  return 'unknown'
}

const LOC_ROOT_SEGMENTS = new Set(['body', 'query', 'path', 'header', 'cookie'])

function fieldPathFromLoc(loc: Array<string | number>): string {
  const segments = [...loc]
  if (
    segments.length > 1 &&
    typeof segments[0] === 'string' &&
    LOC_ROOT_SEGMENTS.has(segments[0])
  ) {
    segments.shift()
  }
  if (segments.length === 0) return '_root'
  return segments.map(String).join('.')
}

function extractFieldErrors(body: ApiErrorBody | undefined): Record<string, string[]> | undefined {
  if (!body?.detail || !Array.isArray(body.detail)) return undefined
  const result: Record<string, string[]> = {}
  for (const item of body.detail) {
    if (!item || !Array.isArray(item.loc) || typeof item.msg !== 'string') continue
    const path = fieldPathFromLoc(item.loc)
    if (!result[path]) result[path] = []
    result[path].push(item.msg)
  }
  return Object.keys(result).length > 0 ? result : undefined
}

function extractStringDetail(body: ApiErrorBody | undefined): string | undefined {
  if (!body?.detail) return undefined
  if (typeof body.detail === 'string' && body.detail.trim().length > 0) {
    return body.detail
  }
  return undefined
}

type HeaderBag = AxiosResponseHeaders | RawAxiosResponseHeaders | undefined

function readHeader(headers: HeaderBag, name: string): string | undefined {
  if (!headers) return undefined
  const raw = (headers as Record<string, unknown>)[name] ?? (headers as Record<string, unknown>)[name.toLowerCase()]
  if (raw == null) return undefined
  if (typeof raw === 'string') return raw
  if (typeof raw === 'number') return String(raw)
  if (Array.isArray(raw) && raw.length > 0) return String(raw[0])
  return undefined
}

function parseRetryAfter(headerValue: string | undefined): number | undefined {
  if (!headerValue) return undefined
  const asNumber = Number(headerValue)
  if (Number.isFinite(asNumber)) return Math.max(0, asNumber) * 1000
  const asDate = Date.parse(headerValue)
  if (Number.isFinite(asDate)) return Math.max(0, asDate - Date.now())
  return undefined
}

function parseNumberHeader(headerValue: string | undefined): number | undefined {
  if (!headerValue) return undefined
  const n = Number(headerValue)
  return Number.isFinite(n) ? n : undefined
}

function summarizePydanticDetail(items: PydanticDetailItem[]): string | undefined {
  const first = items.find((item) => typeof item?.msg === 'string' && item.msg.trim().length > 0)
  if (!first) return undefined
  const path = fieldPathFromLoc(first.loc ?? [])
  return path === '_root' ? first.msg : `${path}: ${first.msg}`
}

export function normalizeAxiosError(error: AxiosError<ApiErrorBody>): ApiError {
  const endpoint = error.config?.url
  const method = error.config?.method?.toUpperCase()

  if (error.code === 'ERR_CANCELED' || error.message === 'canceled') {
    return new ApiError({
      kind: 'canceled',
      status: 0,
      message: FALLBACK_MESSAGES.canceled,
      endpoint,
      method,
      cause: error,
    })
  }

  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return new ApiError({
      kind: 'timeout',
      status: 0,
      message: FALLBACK_MESSAGES.timeout,
      endpoint,
      method,
      cause: error,
    })
  }

  if (!error.response) {
    return new ApiError({
      kind: 'network',
      status: 0,
      message: FALLBACK_MESSAGES.network,
      endpoint,
      method,
      cause: error,
    })
  }

  const status = error.response.status
  const kind = kindFromStatus(status)
  const body = (error.response.data ?? undefined) as ApiErrorBody | undefined
  const headers = error.response.headers as HeaderBag

  const stringDetail = extractStringDetail(body)
  const fieldErrors = extractFieldErrors(body)
  const pydanticSummary = kind === 'validation' && Array.isArray(body?.detail)
    ? summarizePydanticDetail(body!.detail as PydanticDetailItem[])
    : undefined

  const retryAfterMs = parseRetryAfter(readHeader(headers, 'retry-after'))
  const rateLimitLimit = parseNumberHeader(readHeader(headers, 'x-ratelimit-limit'))
  const rateLimitRemaining = parseNumberHeader(readHeader(headers, 'x-ratelimit-remaining'))
  const rateLimit =
    rateLimitLimit !== undefined || rateLimitRemaining !== undefined
      ? { limit: rateLimitLimit, remaining: rateLimitRemaining }
      : undefined

  let message = stringDetail ?? pydanticSummary ?? FALLBACK_MESSAGES[kind]
  if (kind === 'rate_limited' && !stringDetail && retryAfterMs !== undefined) {
    const seconds = Math.max(1, Math.ceil(retryAfterMs / 1000))
    message = `Too many requests. Try again in ${seconds}s.`
  }

  return new ApiError({
    kind,
    status,
    message,
    fieldErrors,
    retryAfterMs,
    rateLimit,
    endpoint,
    method,
    cause: error,
  })
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError
}
